import React, { useMemo, useCallback } from 'react'
import { useTeamColors } from '@/hooks/useTeamColors'
import { TurnStatus } from '@db8/shared'
import { useTurns } from '@/contexts/TurnsProvider'
import { useSingleClick } from '@/hooks/useSingleClick'
import TextLoading from '@/components/ui/textLoading'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import { Button } from '../ui/button'
import { Ban, LoaderCircle, Pause, Play, RotateCcw, SkipForward, Sparkles } from 'lucide-react'
import { useHall } from '@/contexts/HallProvider'

type ItemControlsProps = {
  userId: string
  onPause: () => void
  onPlay: () => void
  onNext: () => void
  onRetry?: () => void
  onRestart: () => void
  isLoading: boolean
  error: string
  isDisabled: boolean
  message: string
  turnType: any
  onCancel: () => void
  regen: () => void
}

export const ItemControls = React.memo(function ItemControls({
  userId,
  onPause,
  onPlay,
  onNext,
  onRetry,
  onRestart,
  isLoading,
  error,
  isDisabled,
  message,
  turnType,
  onCancel,
  regen,
}: ItemControlsProps) {
  const { borderColor } = useTeamColors({ turnType })
  const { currentTurn } = useTurns()
  const { hallMetadata } = useHall()

  // Use a short fallback delay for heavy ops, zero for snappy Play/Pause.
  const singleClick = useSingleClick(600)
  const singleTap = useSingleClick(0)

  const isBot = currentTurn?.primarySpeakerId === userId

  const { isPlaying, isCancelling, isThinking } = useMemo(
    () => ({
      isReady: hallMetadata.status === TurnStatus.READY,
      isPaused: [TurnStatus.PAUSED, TurnStatus.RESTART, TurnStatus.CANCELLED].includes(
        hallMetadata.status,
      ),
      isPlaying: [TurnStatus.PLAYING, TurnStatus.RECORDING].includes(hallMetadata.status),
      isCancelling: hallMetadata.status === TurnStatus.CANCELLED,
      isThinking: hallMetadata.status === TurnStatus.THINKING,
    }),
    [hallMetadata.status],
  )

  // Hide playback/next while the bot is thinking/generating; show only Cancel
  const showOnlyCancel = isThinking && isLoading

  // Stable, memoized wrappers so they don't get recreated every tick.
  const handlePlay = useCallback(() => singleTap(onPlay), [singleTap, onPlay])
  const handlePause = useCallback(() => singleTap(onPause), [singleTap, onPause])
  const handleRestart = useCallback(() => singleClick(onRestart), [singleClick, onRestart])
  const handleNext = useCallback(() => singleClick(onNext), [singleClick, onNext])
  const handleRetry = useCallback(() => onRetry && singleClick(onRetry), [singleClick, onRetry])
  const handleRegen = useCallback(() => singleClick(regen), [singleClick, regen])
  const handleCancel = useCallback(() => singleClick(onCancel), [singleClick, onCancel])

  return (
    <div className="relative flex flex-col items-center w-full">
      {message && (
        <div className="text-xs mb-2 w-full rounded p-1 text-center border" style={{ borderColor }}>
          {isLoading ? <TextLoading text={message} /> : message}
        </div>
      )}

      {showOnlyCancel ? (
        <div className="flex items-center justify-center w-full">
          <CancelControl onCancel={handleCancel} isCancelling={isCancelling} />
        </div>
      ) : (
        <div className="flex items-center justify-between w-full">
          {/* Left: playback */}
          <div className="flex items-center space-x-3">
            {error && (
              <RetryControl
                onRetry={handleRetry}
                isLoading={isLoading}
                isCancelling={isCancelling}
              />
            )}

            <PlayPauseControl
              isPlaying={isPlaying}
              isDisabled={isDisabled}
              isLoading={isLoading}
              onPlay={handlePlay}
              onPause={handlePause}
            />

            <RestartControl onClick={handleRestart} />

            {/* NEW: TTS speed toggle (room-wide) */}
            <TtsRateControl />
          </div>

          {/* Right: content-changing */}
          <div className="flex items-center space-x-3">
            {isBot && <RegenerateControl regen={handleRegen} />}
            <NextControl onClick={handleNext} />
          </div>
        </div>
      )}
    </div>
  )
})

type PlayPauseProps = {
  isPlaying: boolean
  isLoading: boolean
  isDisabled: boolean
  onPlay: () => void
  onPause: () => void
}

export const PlayPauseControl = React.memo(function PlayPauseControl({
  isPlaying,
  isLoading,
  isDisabled,
  onPlay,
  onPause,
}: PlayPauseProps) {
  const Icon = isLoading ? (
    <LoaderCircle className="animate-spin h-4 w-8" />
  ) : isPlaying ? (
    <Pause className="h-4 w-8" />
  ) : (
    <Play className="h-4 w-8" />
  )

  const handler = isPlaying ? onPause : onPlay
  const tooltip = isPlaying ? 'Pause turn' : 'Start turn'

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            className="px-2 py-1 text-xs"
            onClick={handler}
            disabled={isDisabled || isLoading}
          >
            {Icon}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
})

export const RetryControl = React.memo(function RetryControl({
  onRetry,
  isLoading,
  isCancelling,
}: {
  onRetry?: () => void
  isLoading: boolean
  isCancelling: boolean
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            className="px-2 py-1 text-xs"
            onClick={onRetry}
            disabled={isLoading}
          >
            <RotateCcw className="h-4 w-8" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">Retry last generation</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
})

export const CancelControl = React.memo(function CancelControl({
  onCancel,
  isCancelling,
}: {
  onCancel: () => void
  isCancelling: boolean
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="destructive"
            className="px-3 py-1 text-xs"
            onClick={onCancel}
            disabled={isCancelling}
          >
            <Ban size="16px" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">Cancel bot generation</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
})

export const RestartControl = React.memo(function RestartControl({
  onClick,
}: {
  onClick: () => void
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="outline" className="px-2 py-1 text-xs" onClick={onClick}>
            <RotateCcw className="h-4 w-8" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">Restart playback & timer</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
})

export const NextControl = React.memo(function NextControl({ onClick }: { onClick: () => void }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="outline" className="px-2 py-1 text-xs" onClick={onClick}>
            <SkipForward className="h-4 w-8" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">Advance to the next speaker</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
})

export const RegenerateControl = React.memo(function RegenerateControl({
  regen,
}: {
  regen: () => void
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="secondary" className="text-xs px-2 py-1" onClick={regen}>
            <Sparkles className="h-4 w-8" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">Regenerate transcript & TTS</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
})

/** **********************
 * TTS rate toggle button
 * Cycles: 1 → 1.25 → 1.5 → 2 → 1
 *************************/
const RATE_CYCLE = [1, 1.25, 1.5, 2] as const
type Rate = (typeof RATE_CYCLE)[number]

export const TtsRateControl = React.memo(function TtsRateControl() {
  const { isOwner, hallMetadata, setHallMetadata } = useHall()

  const current: Rate = (
    RATE_CYCLE.includes(Number(hallMetadata?.ttsRate) as Rate)
      ? (Number(hallMetadata?.ttsRate) as Rate)
      : 1
  ) as Rate

  const nextRate = (r: Rate): Rate => {
    const idx = RATE_CYCLE.indexOf(r)
    return RATE_CYCLE[(idx + 1) % RATE_CYCLE.length]
  }

  const onClick = useCallback(() => {
    if (!isOwner) return
    setHallMetadata({ ttsRate: nextRate(current) })
  }, [isOwner, current, setHallMetadata])

  const label = `${current}x`

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            className="px-2 py-1 text-xs min-w-12"
            onClick={onClick}
            disabled={!isOwner}
          >
            {label}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">
            {isOwner ? 'Toggle TTS speed for everyone' : 'Only the owner can change speed'}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
})
